//#region Imports and Global Definitions
import React, { useState, useEffect, useContext } from 'react';
import { StyleSheet, View, SafeAreaView, ScrollView, Platform, SectionList, useWindowDimensions, Pressable, Image, ActivityIndicator } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../styles/commonStyles';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../components/CustomButton';
import CustomDropDownWithIcons from "../../../components/CustomDropDownWithIcons/CustomDropDownWithIcons";
import CustomInput from '../../../components/CustomInput';
import { DEFAULTPROFILE } from '../../../img/DefaultProfile';
import { useTranslation } from 'react-i18next';
import { useHRAdminEmployees } from '../../../hooks/useHRAdminEmployees';
import { showMessage } from 'react-native-flash-message';
//#endregion

const HRAdminSelectEmployeeScreen = (props) => {

    const {
        selectedCompany,
        selectedCompanyChanged,
        setSelectedCompanyChanged,
        selectedEmployee,
        setSelectedEmployee,
        branchOptions,
        setBranchOptions,
        selectedBranches,
        setSelectedBranches,
        locationOptions,
        setLocationOptions,
        selectedLocations,
        setSelectedLocations,
        departmentOptions,
        setDepartmentOptions,
        selectedDepartments,
        setSelectedDepartments,
        seniorityLevelOptions,
        setSeniorityLevelOptions,
        selectedSeniorityLevels,
        setSelectedSeniorityLevels,
        employmentTypeOptions,
        setEmploymentTypeOptions,
        selectedEmploymentTypes,
        setSelectedEmploymentTypes,
        jobTitleOptions,
        setJobTitleOptions,
        selectedJobTitles,
        setSelectedJobTitles,
        selectAllInactive,
        setSelectAllInactive,
        statusOptions,
        employees,
        setEmployees,
        searchQuery,
        setSearchQuery,
        selectedCompanyCode,
        companyOptions,
        setSelectedCompanyCode
    } = props;

    const { width, height } = useWindowDimensions();
    const isPlatformWeb = Platform.OS === 'web';
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const { t } = useTranslation();
    const { getBranches, getLocations, getDepartments, getSeniorityLevels, getEmploymentTypes, getJobTitles, getEmployees } = useHRAdminEmployees();

    const [isLoading, setIsLoading] = useState(true);
    const [findEmployeesPressed, setFindEmployeesPressed] = useState(false);

    // temp arrays to store dropdown options
    let branchOptionsTemp = [];
    let locationOptionsTemp = [];
    let departmentOptionsTemp = [];
    let seniorityLevelOptionsTemp = [];
    let employmentTypeOptionsTemp = [];
    let jobTitleOptionsTemp = [];

    //#region Helper Functions
    const resetSelections = () => {
        // setSelectedEmployee(undefined);
        setSelectedBranches([]);
        setSelectedLocations([]);
        setSelectedDepartments([]);
        setSelectedSeniorityLevels([]);
        setSelectedEmploymentTypes([]);
        setSelectedJobTitles([]);
        setSelectAllInactive('All');
        setEmployees([]);
        setSearchQuery(undefined);
        setFindEmployeesPressed(false);
    };

    const resetDropdownOptions = () => {
        setBranchOptions([]);
        setLocationOptions([]);
        setDepartmentOptions([]);
        setSeniorityLevelOptions([]);
        setEmploymentTypeOptions([]);
        setJobTitleOptions([]);
    };

    const groupDataByLetter = (data) => {
        // Separate items with empty name and non-empty name
        const itemsWithName = data.filter(item => item.name && item.name.trim() !== "");
        const itemsWithoutName = data.filter(item => !item.name || item.name.trim() === "");

        // Group items with name by first letter
        const grouped = itemsWithName.reduce((acc, item) => {
            const firstLetter = item.name[0].toUpperCase();
            if (!acc[firstLetter]) acc[firstLetter] = [];
            acc[firstLetter].push(item);
            return acc;
        }, {});

        // Create sorted grouped array
        const groupedArray = Object.keys(grouped)
            .sort()
            .map(letter => ({
                title: letter,
                data: grouped[letter],
            }));

        // Group with employees that do not have name
        if (itemsWithoutName.length > 0) {
            groupedArray.push({
                title: '#',
                data: itemsWithoutName,
            });
        }

        return groupedArray;
    };

    const onFindEmployeesPressed = () => {
        setSearchQuery(undefined);
        findEmployees();
    };
    //#endregion

    //#region Get Functions
    const fillDropdowns = async () => {
        try {
            setIsLoading(true);
            if (selectedCompany?.code) {
                // Get Branches
                getBranches(selectedCompany).then((data) => {
                    data.forEach((obj) => {
                        branchOptionsTemp.push({ label: obj.description, value: obj.branch });
                    });
                    setBranchOptions(branchOptionsTemp);

                    // Get Locations
                    getLocations(selectedCompany).then((data) => {
                        data.forEach((obj) => {
                            locationOptionsTemp.push({ label: obj.description, value: obj.location });
                        });
                        setLocationOptions(locationOptionsTemp);

                        // Get Departments
                        getDepartments(selectedCompany).then((data) => {
                            data.forEach((obj) => {
                                departmentOptionsTemp.push({ label: obj.description, value: obj.dept });
                            });
                            setDepartmentOptions(departmentOptionsTemp);

                            // Get Seniority Levels
                            getSeniorityLevels(selectedCompany).then((data) => {
                                data.forEach((obj) => {
                                    seniorityLevelOptionsTemp.push({ label: obj.description, value: obj.hcode });
                                });
                                setSeniorityLevelOptions(seniorityLevelOptionsTemp);

                                // Get Employment Types
                                getEmploymentTypes(selectedCompany).then((data) => {
                                    data.forEach((obj) => {
                                        employmentTypeOptionsTemp.push({ label: obj.description, value: obj.etype });
                                    });
                                    setEmploymentTypeOptions(employmentTypeOptionsTemp);

                                    // Get Job Titles
                                    getJobTitles(selectedCompany).then((data) => {
                                        data.forEach((obj) => {
                                            jobTitleOptionsTemp.push({ label: obj.description, value: obj.jobtitle });
                                        });
                                        setJobTitleOptions(jobTitleOptionsTemp);
                                    })
                                        .catch((err) => {
                                            console.log(err.message);
                                        });
                                })
                                    .catch((err) => {
                                        console.log(err.message);
                                    });
                            })
                                .catch((err) => {
                                    console.log(err.message);
                                });
                        })
                            .catch((err) => {
                                console.log(err.message);
                            });
                    })
                        .catch((err) => {
                            console.log(err.message);
                        });
                })
                    .catch((err) => {
                        console.log(err.message);
                    });
            };
        }
        catch (error) {
            console.error(error)
        }
        finally {
            setIsLoading(false);
        };
    };

    const findEmployees = (searchQuery = "") => {
        setIsLoading(true);
        setFindEmployeesPressed(true);

        getEmployees(selectedBranches,
            selectedLocations,
            selectedDepartments,
            selectedSeniorityLevels,
            selectedEmploymentTypes,
            selectedJobTitles,
            searchQuery,
            selectedCompanyCode,
            true,
            selectAllInactive)
            .then((data) => {
                setEmployees(groupDataByLetter(data));
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err.message);
                setIsLoading(false);
            });
    };
    //#endregion

    //#region UseEffects
    // selected company changed -> reset, and re-fill dropdowns
    useEffect(() => {
        if (selectedCompanyChanged) {
            setSelectedCompanyChanged(false);
            resetSelections();
            resetDropdownOptions();
            fillDropdowns();
        };
    }, [selectedCompanyChanged]);

    useEffect(() => {
        // if (!selectedCompany?.code) {
        //     showMessage({
        //         message: t("common:selectCompany"),
        //         type: "info",
        //         duration: 3000,
        //     });
        //     return;
        // };
    }, [selectedCompany?.code]);

    // search query changed -> find employees
    useEffect(() => {
        if (searchQuery !== undefined) {
            const delayDebounceFn = setTimeout(() => {
                findEmployees(searchQuery);
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        } else {
            setIsLoading(false);
        };
    }, [searchQuery]);

    useEffect(() => { findEmployees() }, [])
    //#endregion

    // if (isLoading)
    //     return null;
    return (
        <SafeAreaView style={styles.mainView}>
            <ScrollView>
                <SafeAreaView style={styles.row}>
                    {/* left column */}
                    <SafeAreaView style={[styles.filters, common.cardstyle]}>
                        {/* dropdowns */}
                        {/* Employee Status */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 8 }]}>
                            <CustomLabel
                                value={t('common:status')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15} />
                            <CustomDropDownWithIcons
                                defValue={selectAllInactive}
                                options={statusOptions}
                                setValue={setSelectAllInactive}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>
                        {/* company */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 7 }]}>
                            <CustomLabel
                                value={t('navigate:hrAdminCompany')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15} />
                            <CustomDropDownWithIcons
                                defValue={selectedCompanyCode}
                                options={companyOptions}
                                setValue={setSelectedCompanyCode}
                                type="SELECT_EMPLOYEE"
                            />
                        </SafeAreaView>
                        {/* branches */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 6 }]}>
                            <CustomLabel
                                value={t('common:branch')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15}
                            />
                            <CustomDropDownWithIcons
                                defValue={selectedBranches}
                                options={branchOptions}
                                setValue={setSelectedBranches}
                                multiple={true}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                        {/* locations */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 5 }]}>
                            <CustomLabel
                                value={t('common:location')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15}
                            />
                            <CustomDropDownWithIcons
                                defValue={selectedLocations}
                                options={locationOptions}
                                setValue={setSelectedLocations}
                                multiple={true}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                        {/* departments */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 4 }]}>
                            <CustomLabel
                                value={t('common:department')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15}
                            />
                            <CustomDropDownWithIcons
                                defValue={selectedDepartments}
                                options={departmentOptions}
                                setValue={setSelectedDepartments}
                                multiple={true}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                        {/* seniority levels */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 3 }]}>
                            <CustomLabel
                                value={t('common:seniorityLevel')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15}
                            />
                            <CustomDropDownWithIcons
                                defValue={selectedSeniorityLevels}
                                options={seniorityLevelOptions}
                                setValue={setSelectedSeniorityLevels}
                                multiple={true}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                        {/* employment types */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 2 }]}>
                            <CustomLabel
                                value={t('common:employmentType')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15}
                            />
                            <CustomDropDownWithIcons
                                defValue={selectedEmploymentTypes}
                                options={employmentTypeOptions}
                                setValue={setSelectedEmploymentTypes}
                                multiple={true}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                        {/* job titles */}
                        <SafeAreaView style={[styles.dropdownView, { zIndex: 1 }]}>
                            <CustomLabel
                                value={t('common:jobTitle')}
                                type='AVAILABLEUSED_SECONDARY'
                                align='flex-start'
                                fontSize={15} />
                            <CustomDropDownWithIcons
                                defValue={selectedJobTitles}
                                options={jobTitleOptions}
                                setValue={setSelectedJobTitles}
                                multiple={true}
                                disabled={selectedCompany?.code ? false : true}
                                type='SELECT_EMPLOYEE'
                            />
                        </SafeAreaView>

                        {/* buttons */}
                        <SafeAreaView style={styles.buttons}>
                            <CustomButton
                                text={t('common:reset')}
                                onPress={() => resetSelections()}
                                disabled={selectedCompany?.code ? false : true}
                                type="BUTTON_ADD"
                            />
                            <CustomButton
                                text={t('common:apply')}
                                onPress={() => onFindEmployeesPressed()}
                                disabled={selectedCompany?.code ? false : true}
                                type="BUTTON_SAVE"
                                isGradient={true}
                            />
                        </SafeAreaView>
                    </SafeAreaView>

                    {/* right column */}
                    <SafeAreaView style={styles.employeeList}>
                        <SafeAreaView style={[common.cardstyle, styles.search]}>
                            {/* search */}
                            <CustomInput
                                value={searchQuery || ""}
                                editable={selectedCompany?.code ? true : false}
                                setValue={setSearchQuery}
                                iconName="search-outline"
                                iconSize={18}
                                show="SEARCH"
                                type='SEARCH'
                                placeholder={`${t('common:search')}...`}
                            />
                            <SafeAreaView style={common.divider} />
                            <CustomLabel
                                value={searchQuery ? `${t('common:searchResults')}${searchQuery}` : t('common:searchResults')}
                                type={'SELECT_EMPLOYEE_SELECTED_COMPANY'}
                            />
                            <SafeAreaView style={common.divider} />
                        </SafeAreaView>
                        {/* card with list of employees */}
                        <SafeAreaView style={[common.cardstyle, styles.employeeSearchList]}>
                            {isLoading
                                ?
                                <SafeAreaView style={styles.cardEmployees}>
                                    <ActivityIndicator animating={true} color={colors.blue} />
                                </SafeAreaView>
                                :
                                employees.length > 0 && selectedCompany?.code
                                    ?
                                    <SectionList
                                        sections={employees}
                                        keyExtractor={(item, index) => item.employeeID}
                                        initialNumToRender={30}
                                        contentContainerStyle={{
                                            paddingHorizontal: 10,
                                            maxHeight: height * 0.5,
                                        }}
                                        renderItem={({ item }) => (
                                            <SafeAreaView>
                                                <Pressable
                                                    onPress={() => setSelectedEmployee(item)}
                                                    style={() => [item.employeeID === selectedEmployee?.employeeID ? styles.selectedItem : null]}
                                                >
                                                    <SafeAreaView style={styles.employeeContainer}>
                                                        <Image
                                                            style={styles.employeePhotograph}
                                                            source={{ uri: `data:image/png;base64,${item.photograph || DEFAULTPROFILE}` }}
                                                        />
                                                        <SafeAreaView style={[common.listTitle, { marginHorizontal: 10, }]}>
                                                            <CustomLabel
                                                                value={item.name}
                                                                type='AVAILABLEUSED_SECONDARY'
                                                                align='flex-start'
                                                            />
                                                            <CustomLabel
                                                                value={item.seniority}
                                                                type='AVAILABLEUSED_MUTED'
                                                                align='flex-start'
                                                            />
                                                            <CustomLabel
                                                                value={item.status}
                                                                type='AVAILABLEUSED_MUTED'
                                                                align='flex-start'
                                                            />
                                                        </SafeAreaView>
                                                    </SafeAreaView>
                                                    <SafeAreaView style={common.divider} />
                                                </Pressable>
                                            </SafeAreaView>
                                        )}
                                        renderSectionHeader={({ section: { title } }) => (
                                            <CustomLabel
                                                value={title}
                                                type='MAJOR' />
                                        )}
                                    />
                                    :
                                    searchQuery || findEmployeesPressed
                                        ?
                                        <SafeAreaView
                                            style={styles.cardEmployees}>
                                            <CustomLabel
                                                value={t("common:noResultsFound")}
                                                type="ASSM"
                                            />
                                        </SafeAreaView>
                                        :
                                        null
                            }
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </ScrollView >
        </SafeAreaView >
    )
};

//#region Styles
const makeStyles = (colors, isPlatformWeb) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginLeft: '3%',
        marginRight: '3%',
        marginBottom: '3%',
        height: '100%',
    },

    row: {
        flex: 1,
        flexDirection: "row",
    },
    CheckBoxRow: {
        flexDirection: "row",
        justifyContent: 'flex-start',
        marginTop: 15,
        marginRight: 15,
    },
    filters: {
        flex: 1,
        padding: 10,
    },
    employeeList: {
        flex: 3,
        margin: 10,
    },
    search: {
        backgroundColor: colors.background,
        marginHorizontal: 10,
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        flex: 1,
        margin: 15,
    },

    dropdownView: {
        flex: 1,
        margin: 10,
    },

    employeeSearchList: {
        flex: 1,
        margin: 10,
    },

    cardEmployees: {
        flex: 1,
        marginTop: "1%",
        marginLeft: "2%",
        marginRight: "2%",
    },

    selectedItem: {
        borderWidth: 4,
        borderColor: colors.buttons_primary,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
    },

    employeeContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        margin: 5,
    },

    employeePhotograph: {
        width: 30,
        height: 30,
        marginLeft: 10,
        borderRadius: 100,
        borderWidth: 1,
        verticalAlign: 'middle',
    },

});

//#endregion

export default HRAdminSelectEmployeeScreen;